import { Recurrence, SelfServiceSchedulePane, StaticInitializers } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { ButtonType, DialogFooterButtonsWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { IExtractTaskInfo } from '~src/extractTask/models/IExtractTaskInfo';
import { ExtractTasks } from '~src/extractTask/resources/ExtractTasks';
import { ExtractTaskType } from '~src/extractTask/serverApi/types/IExtractTask';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import {
  getUserTimeZoneDetails,
  isExtractSSSEnabled
} from '~src/publishedConnection/utils/PublishedConnectionTaskUtil';
import { ExtractServiceRefreshType } from '~src/schedule/serverApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { IScheduleInfo } from '~src/schedule/types/IScheduleInfo';
import { cssClass } from '~src/style';

import { PublishedConnections } from '../../resources/PublishedConnections';
import { PublishedConnectionTaskSchedulePicker } from './PublishedConnectionTaskSchedulePicker';

const cssPrefix = 'PublishedConnectionEditScheduleDialogContent';

const rootClass = cssClass(cssPrefix, 'root', {
  padding: `0 ${CommonStyles.Sizes.SmallSpace}px ${CommonStyles.Sizes.LargeSpace}px`
});

const dialogMessageClass = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: TabStyles.Spacing.LargeSpace,
  wordWrap: 'break-word'
});

const frequencyHeaderClass = cssClass(cssPrefix, 'frequencyHeader', {
  fontWeight: 'normal',
  fontSize: 16,
  margin: '0',
  height: '20px',
  lineHeight: '20px',
  paddingBottom: '8px',
});

const frequencyPaneClass = cssClass(cssPrefix, 'frequencyPane', {
  maxWidth: 500,
  marginTop: '-36px',
  marginBottom: '18px'
});

function renderSelfServiceSchedulePane(
  recurrence: Recurrence,
  onRecurrenceChange: (model: Recurrence) => void,
  dismiss: () => void
) {

  return (
    <>
      <h1 className={frequencyHeaderClass}>{Messages.refreshSelfServiceScheduleRefreshFrequency()}</h1>
      <span data-tb-test-id='edit-published-connection-schedule-description'>
          {IScheduleInfo.getRecurrenceDescription(recurrence)}
      </span>
      <div className={frequencyPaneClass}>
        <SelfServiceSchedulePane
          recurrenceModel={recurrence}
          recurrenceModelChangedCallback={onRecurrenceChange}
          timeZoneDetails={getUserTimeZoneDetails()}
          type={SelfServiceSchedulePaneType.Default}
          uncollapsible={true}
          onTimeZoneClick={dismiss}
        />
      </div>
    </>
  );
}

function renderAdminSchedulePane(
  taskCount: number,
  selectedSchedule: string,
  onSelectSchedule: (value: string) => void
) {

  return (
    <>
      <div className={dialogMessageClass}>
        {Messages.changeScheduleExtractTasksPrompt({ COUNT_LOC: L10n.localizedCount(taskCount) })}
      </div>
      <PublishedConnectionTaskSchedulePicker
        scheduleId={selectedSchedule}
        onSelect={onSelectSchedule}
      />
    </>
  );
}

function handleResult(result: IResult, close: () => void, taskCount: number) {
  const errorMessage = result.errors
    ? Messages.changeSchedulePublishedConnectionTasksError({
      ERROR_COUNT: result.errors.length,
      ERROR_COUNT_LOC: Formatters.formatCount(result.errors.length)
    })
    : undefined;
  PublishedConnections.toast(result,
    Messages.changeSchedulePublishedConnectionTasksSuccess({
      ITEM_COUNT: taskCount,
      ITEM_COUNT_LOC: Formatters.formatCount(taskCount)
    }),
    errorMessage
  );
  close();
}

export const PublishedConnectionEditScheduleDialogContent: React.FC<{
  close: () => void;
  dismiss: () => void;
  tasks: ReadonlyArray<IExtractTaskInfo>;
}> = (
  {
    close,
    dismiss,
    tasks
  }) => {
  const [selectedSchedule, setSelectedSchedule] = React.useState('');
  const handleSelectSchedule = React.useCallback((value) => setSelectedSchedule(value), []);
  const [recurrence, setRecurrence] = React.useState<Recurrence>(StaticInitializers.defaultRecurrence());
  const selfServiceEnabled = isExtractSSSEnabled();

  const handleRecurrenceChange = React.useCallback(
    (newRecurrence) => setRecurrence(newRecurrence), [setRecurrence]
  );

  const handleEditSchedule = React.useCallback(
    () => {
      if (selfServiceEnabled) {
        PublishedConnections.setExtractTasksSelfServiceSchedule(
          tasks.map(task => parseInt(task.id)),
          recurrence,
          ExtractTaskType.ExtractServiceFullRefresh as ExtractServiceRefreshType,
        ).then(result => handleResult(result, close, tasks.length));
      } else {
        ExtractTasks.setSchedule(tasks.map(task => task.id), selectedSchedule)
          .then(result => handleResult(result, close, tasks.length));
      }
    },
    [close, recurrence, selectedSchedule, selfServiceEnabled, tasks]
  );

  return (
    <div>
      <div className={rootClass}>
        {selfServiceEnabled
          ? renderSelfServiceSchedulePane(recurrence, handleRecurrenceChange, dismiss)
          : renderAdminSchedulePane(tasks.length, selectedSchedule, handleSelectSchedule)
        }
      </div>

      <DialogFooterButtonsWidget
        buttons={[{
          handleClick: dismiss,
          buttonType: ButtonType.Outline,
          children: Messages.cancel(),
          testId: 'edit-published-connection-schedule-cancel'
        }, {
          handleClick: handleEditSchedule,
          buttonType: ButtonType.Primary,
          children: Messages.changeSubscriptionsFrequencyConfirm({
            COUNT: tasks.length,
            COUNT_LOC: L10n.localizedCount(tasks.length)
          }),
          testId: 'edit-published-connection-schedule-go',
          disabled: !selfServiceEnabled && !selectedSchedule
        }]}
        testId={'edit-published-connection-schedule-footer'}
      />
    </div>
  );
};
PublishedConnectionEditScheduleDialogContent.displayName = 'PublishedConnectionEditScheduleDialogContent';
